import { Box, Button } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Avatar from '@mui/material/Avatar'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import SendIcon from '@mui/icons-material/Send'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { makeGetRequest, makePostRequest, makePatchRequest } from 'utils/api'
import blogo from 'Components/images/blogo.png'
import CreateDocForm from './CreateDocForm'
import CreateMessage from './CreateMessage'

/* ============ Palette (trame mockups Life) ============ */
const C = {
  page: '#03102e',
  card: '#0d1f4b',
  card2: '#122a63',
  inner: '#0a1a40',
  txt: '#ffffff',
  muted: '#8fa3cc',
  muted2: '#c1d0eb',
  green: '#4ade80',
  red: '#f87171',
  amber: '#fbbf24',
  blue: '#5b9bf5',
  lblue: '#a8c6f0',
  violet: '#a78bfa',
  pink: '#f472b6',
  cyan: '#22d3ee',
  orange: '#fb923c',
  border: 'rgba(255,255,255,0.07)',
  btn: '#2d5fb8',
}

/* ============ Config par type d'objet partagé (setting_id) ============ */
const OBJECT_TYPES = {
  1: { icon: '📅', color: C.amber, bg: 'rgba(251,191,36,.15)', route: () => '/MonCalendrier' },
  2: { icon: '📄', color: C.orange, bg: 'rgba(251,146,60,.15)', route: () => '/documents' },
  3: {
    icon: '🏠',
    color: C.cyan,
    bg: 'rgba(34,211,238,.14)',
    route: (id) => (id ? `/domicile/monimmobilier/monbien/${id}` : '/domicile/monimmobilier'),
  },
  4: {
    icon: '🚗',
    color: C.cyan,
    bg: 'rgba(34,211,238,.14)',
    route: (id) => (id ? `/domicile/vehicule/${id}` : '/domicile/mesvehicules'),
  },
  5: {
    icon: '💰',
    color: C.green,
    bg: 'rgba(74,222,128,.14)',
    route: (id) => (id ? `/compte/${id}` : '/mescomptes'),
  },
  6: {
    icon: '🍲',
    color: C.violet,
    bg: 'rgba(167,139,250,.16)',
    route: (id) => (id ? `/recette/${id}` : '/recette'),
  },
  7: { icon: '🍽️', color: C.violet, bg: 'rgba(167,139,250,.16)', route: () => '/Menu' },
  8: { icon: '📝', color: C.blue, bg: 'rgba(91,155,245,.16)', route: () => '/todos' },
  9: {
    icon: '🎯',
    color: C.blue,
    bg: 'rgba(91,155,245,.16)',
    route: () => '/todos?filter=projects',
  },
  10: { icon: '✔️', color: C.blue, bg: 'rgba(91,155,245,.16)', route: () => '/todos' },
  11: { icon: '🗒️', color: C.violet, bg: 'rgba(167,139,250,.16)', route: () => '/todos' },
  12: { icon: '👤', color: C.pink, bg: 'rgba(244,114,182,.15)', route: () => '/MesContacts' },
}
const getObjType = (settingId) =>
  OBJECT_TYPES[settingId] || { icon: '📦', color: C.lblue, bg: 'rgba(168,198,240,.15)', route: () => null }

/* Couleur d'avatar stable à partir du nom */
const AVATAR_COLORS = [
  { bg: 'rgba(167,139,250,.25)', fg: C.violet },
  { bg: 'rgba(91,155,245,.22)', fg: C.blue },
  { bg: 'rgba(74,222,128,.2)', fg: C.green },
  { bg: 'rgba(244,114,182,.18)', fg: C.pink },
  { bg: 'rgba(34,211,238,.18)', fg: C.cyan },
  { bg: 'rgba(251,146,60,.18)', fg: C.orange },
]
const avatarColor = (name = '') => {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 997
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}
const initials = (u) =>
  `${(u?.prenom || '?')[0] || ''}${(u?.nom || '')[0] || ''}`.toUpperCase()

const UserAvatar = ({ user, size = 38 }) => {
  const cc = avatarColor(`${user?.prenom} ${user?.nom}`)
  if (user?.img && user?.img_url) {
    return <Avatar src={user.img_url} sx={{ width: size, height: size }} />
  }
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: cc.bg,
        color: cc.fg,
        fontSize: size * 0.37,
        fontWeight: 700,
      }}
    >
      {initials(user)}
    </Avatar>
  )
}

const fmtWhen = (ts) => {
  const m = moment(ts)
  if (m.isSame(moment(), 'day')) return m.format('HH:mm')
  if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Hier'
  if (m.isAfter(moment().subtract(7, 'day'))) return m.format('ddd')
  return m.format('D MMM')
}

/* Extrait les paires field/value non vides d'un message_object */
const objFields = (o) => {
  const out = []
  for (let i = 1; i <= 10; i++) {
    if (o?.[`field${i}`] && o?.[`value${i}`]) out.push({ k: o[`field${i}`], v: o[`value${i}`] })
  }
  return out
}

export default function MyInbox() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const siteUser = useSelector((store) => store.user)
  const [searchParams] = useSearchParams()

  const [messageList, setMessageList] = useState({ inbox: [], outbox: [] })
  const [folder, setFolder] = useState('inbox') // inbox | outbox | done
  const [segment, setSegment] = useState('all') // all | unread | important | objects
  const [typeFilter, setTypeFilter] = useState(null) // object_type
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [comments, setComments] = useState([])
  const [attachments, setAttachments] = useState([])
  const [mOpen, setMOpen] = useState(false)
  const [commentInput, setCommentInput] = useState('')
  const deepLinked = useRef(false)

  useEffect(() => {
    getMessages()
  }, [])

  const getMessages = async () => {
    const res = await makeGetRequest('/api/inbox/messages/', {}, true)
    if (res.status === 200) setMessageList(res.data)
  }

  /* Deep-link ?m=<id> */
  useEffect(() => {
    if (deepLinked.current) return
    const m = parseInt(searchParams.get('m'))
    if (m && (messageList.inbox?.length || messageList.outbox?.length)) {
      deepLinked.current = true
      if (messageList.inbox?.find((e) => e.id === m)) {
        setFolder('inbox')
        openMessage(messageList.inbox.find((e) => e.id === m))
      } else if (messageList.outbox?.find((e) => e.id === m)) {
        setFolder('outbox')
        openMessage(messageList.outbox.find((e) => e.id === m))
      }
    }
  }, [messageList]) // eslint-disable-line react-hooks/exhaustive-deps

  const isInboxMsg = (msg) => messageList.inbox?.some((e) => e.id === msg.id)

  const patchStatus = async (msg, status) => {
    // mise à jour optimiste
    setMessageList((prev) => ({
      inbox: prev.inbox?.map((e) => (e.id === msg.id ? { ...e, status } : e)),
      outbox: prev.outbox?.map((e) => (e.id === msg.id ? { ...e, status } : e)),
    }))
    await makePatchRequest(`/api/inbox/message/${msg.id}/status/`, { status }, {}, true)
  }

  const openMessage = (msg) => {
    setSelectedId(msg.id)
    getComments(msg.id)
    getAttachments(msg.id)
    // marquage lu automatique à l'ouverture (côté destinataire uniquement)
    if (msg.status === 'non lu' && isInboxMsg(msg)) patchStatus(msg, 'lu')
  }

  const getComments = async (id) => {
    const res = await makeGetRequest(`/api/inbox/message/comments/${id}/`, {}, true)
    if (res.status === 200) setComments(res.data)
  }
  const getAttachments = async (id) => {
    const res = await makeGetRequest(`/api/inbox/message/attatchment/${id}/`, {}, true)
    if (res.status === 200) setAttachments(res.data)
  }
  const submitComment = async () => {
    if (!commentInput.trim() || !selectedId) return
    const { data, status } = await makePostRequest(
      '/api/inbox/message/comment/',
      { content: commentInput, message_id: selectedId },
      {},
      true,
    )
    if (status === 200) {
      setComments((pre) => [...pre, data])
      setCommentInput('')
    }
  }

  /* ============ Listes dérivées ============ */
  const allCurrent = folder === 'outbox' ? messageList.outbox || [] : messageList.inbox || []
  const baseList =
    folder === 'done'
      ? (messageList.inbox || []).filter((m) => m.status === 'traité')
      : folder === 'inbox'
      ? (messageList.inbox || []).filter((m) => m.status !== 'traité')
      : messageList.outbox || []

  const unreadCount = (messageList.inbox || []).filter((m) => m.status === 'non lu').length
  const doneCount = (messageList.inbox || []).filter((m) => m.status === 'traité').length

  /* Types d'objets présents (filtres dynamiques) */
  const presentTypes = useMemo(() => {
    const map = {}
    ;(allCurrent || []).forEach((m) => {
      if (m.message_obj && m.message_object?.object_type) {
        const key = m.message_object.object_type
        if (!map[key]) map[key] = { ...getObjType(m.message_object.setting_id), count: 0 }
        map[key].count += 1
      }
    })
    return map
  }, [allCurrent])

  const filtered = baseList.filter((m) => {
    if (segment === 'unread' && m.status !== 'non lu') return false
    if (segment === 'important' && !m.message_object?.is_important) return false
    if (segment === 'objects' && !m.message_obj) return false
    if (typeFilter && m.message_object?.object_type !== typeFilter) return false
    if (search) {
      const other = folder === 'outbox' ? m.u_recipient : m.u_sender
      const hay = `${m.subject} ${m.content || ''} ${m.message_object?.default_subject || ''} ${
        m.message_object?.object_type || ''
      } ${other?.prenom || ''} ${other?.nom || ''}`.toLowerCase()
      if (!hay.includes(search.toLowerCase())) return false
    }
    return true
  })

  const selectedMessage =
    (messageList.inbox || []).find((m) => m.id === selectedId) ||
    (messageList.outbox || []).find((m) => m.id === selectedId)

  /* ============ Sous-composants inline ============ */
  const Folder = ({ id, icon, label, count, countColor }) => (
    <Box
      onClick={() => {
        setFolder(id)
        setSegment('all')
        setTypeFilter(null)
        setSelectedId(null)
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        p: '9px 12px',
        borderRadius: '9px',
        fontSize: 13,
        fontWeight: 600,
        color: folder === id ? '#fff' : C.muted2,
        bgcolor: folder === id ? 'rgba(91,155,245,.18)' : 'transparent',
        cursor: 'pointer',
        '&:hover': { bgcolor: folder === id ? 'rgba(91,155,245,.18)' : 'rgba(255,255,255,.06)' },
      }}
    >
      <span style={{ width: 18, textAlign: 'center' }}>{icon}</span> {label}
      {count > 0 && (
        <Box
          sx={{
            ml: 'auto',
            fontSize: 11,
            fontWeight: 700,
            bgcolor: countColor || 'rgba(255,255,255,.1)',
            color: countColor ? '#fff' : C.muted2,
            borderRadius: '10px',
            px: '8px',
            py: '1px',
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  )

  const Seg = ({ id, label }) => (
    <Box
      onClick={() => setSegment(id)}
      sx={{
        fontSize: 11,
        fontWeight: 600,
        px: '11px',
        py: '5px',
        borderRadius: '11px',
        cursor: 'pointer',
        bgcolor: segment === id ? 'rgba(91,155,245,.22)' : 'rgba(255,255,255,.05)',
        color: segment === id ? '#fff' : C.muted2,
        border: segment === id ? '1px solid rgba(91,155,245,.5)' : '1px solid transparent',
        whiteSpace: 'nowrap',
        '&:hover': { bgcolor: segment === id ? 'rgba(91,155,245,.22)' : 'rgba(255,255,255,.1)' },
      }}
    >
      {label}
    </Box>
  )

  const ActionBtn = ({ children, primary, onClick }) => (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        bgcolor: primary ? C.btn : C.inner,
        border: `1px solid ${primary ? 'transparent' : C.border}`,
        color: primary ? '#fff' : C.muted2,
        borderRadius: '9px',
        fontSize: 11.5,
        fontWeight: 600,
        p: '7px 12px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        fontFamily: 'inherit',
        '&:hover': { color: '#fff', borderColor: 'rgba(91,155,245,.5)', filter: primary ? 'brightness(1.15)' : 'none' },
      }}
    >
      {children}
    </Box>
  )

  const ObjectCard = ({ obj, primary }) => {
    const tp = getObjType(obj.setting_id)
    const fields = objFields(obj)
    const target = tp.route(obj.object_id)
    const name = obj.default_subject || obj.object_type
    return (
      <Box
        sx={{
          background: `linear-gradient(160deg, ${C.card} 0%, ${C.card2} 100%)`,
          border: `1px solid ${tp.bg.replace('.15', '.35').replace('.14', '.35').replace('.16', '.35')}`,
          borderRadius: '14px',
          p: '18px 20px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: tp.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 21,
            }}
          >
            {tp.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 10.5, letterSpacing: '1.2px', fontWeight: 700, color: tp.color }}>
              {(obj.object_type || '').toUpperCase()}
            </Box>
            <Box sx={{ fontSize: 16.5, fontWeight: 700, mt: '1px' }}>{name}</Box>
          </Box>
          {target && (
            <Box sx={{ ml: 'auto' }}>
              <ActionBtn primary onClick={() => navigate(target)}>
                {t('inbox.openObject', 'Ouvrir')} {obj.object_type} →
              </ActionBtn>
            </Box>
          )}
        </Box>
        {fields.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
              gap: '10px',
              mt: '15px',
            }}
          >
            {fields.map((f, i) => (
              <Box
                key={i}
                sx={{ bgcolor: C.inner, border: `1px solid ${C.border}`, borderRadius: '9px', p: '8px 11px' }}
              >
                <Box sx={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>
                  {f.k.replace(/\s*:\s*$/, '').toUpperCase()}
                </Box>
                <Box sx={{ fontSize: 12.5, fontWeight: 600, mt: '2px' }}>{f.v}</Box>
              </Box>
            ))}
          </Box>
        )}
        {primary && (
          <Box sx={{ mt: '13px', fontSize: 11, color: C.muted }}>
            {t('inbox.snapshotHint', "Aperçu au moment du partage · l'original peut avoir évolué")}
          </Box>
        )}
      </Box>
    )
  }

  const SectionTitle = ({ children }) => (
    <Box sx={{ fontSize: 12, letterSpacing: '1.1px', color: C.muted, fontWeight: 700, mb: '10px' }}>
      {children}
    </Box>
  )

  /* ============ Rendu ============ */
  const otherUser = (msg) => (folder === 'outbox' ? msg.u_recipient : msg.u_sender)

  return (
    <>
      {mOpen && <CreateMessage setMOpen={setMOpen} updateData={getMessages} />}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '210px 360px 1fr' },
          height: 'calc(100vh - 80px)',
          bgcolor: C.page,
          color: C.txt,
          overflow: 'hidden',
        }}
      >
        {/* ===== Colonne 1 : dossiers & filtres ===== */}
        <Box
          sx={{
            borderRight: `1px solid ${C.border}`,
            p: '18px 14px',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            gap: '4px',
            overflowY: 'auto',
          }}
        >
          <Box
            component="button"
            onClick={() => setMOpen(true)}
            sx={{
              bgcolor: C.btn,
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              p: '11px 16px',
              fontSize: 13.5,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              mb: '14px',
              fontFamily: 'inherit',
              '&:hover': { filter: 'brightness(1.15)' },
            }}
          >
            <EditIcon sx={{ fontSize: 16 }} /> {t('inbox.newMessage', 'Nouveau message')}
          </Box>
          <Folder id="inbox" icon="📥" label={t('inbox.received', 'Reçus')} count={unreadCount} countColor={C.blue} />
          <Folder id="outbox" icon="📤" label={t('inbox.sent', 'Envoyés')} count={(messageList.outbox || []).length} />
          <Folder id="done" icon="✅" label={t('inbox.processed', 'Traités')} count={doneCount} />

          {Object.keys(presentTypes).length > 0 && (
            <>
              <Box sx={{ m: '12px 4px 6px', fontSize: 10, letterSpacing: '1.4px', color: C.muted, fontWeight: 700 }}>
                {t('inbox.byObject', 'PAR OBJET')}
              </Box>
              {Object.entries(presentTypes).map(([type, tp]) => (
                <Box
                  key={type}
                  onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    p: '7px 12px',
                    borderRadius: '9px',
                    fontSize: 12.5,
                    color: typeFilter === type ? '#fff' : C.muted2,
                    bgcolor: typeFilter === type ? 'rgba(91,155,245,.18)' : 'transparent',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(255,255,255,.06)' },
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tp.color }} />
                  {type}
                  <Box sx={{ ml: 'auto', fontSize: 10.5, color: C.muted }}>{tp.count}</Box>
                </Box>
              ))}
            </>
          )}
        </Box>

        {/* ===== Colonne 2 : liste ===== */}
        <Box sx={{ borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ p: '16px 16px 10px' }}>
            <Box sx={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              {folder === 'inbox'
                ? t('inbox.received', 'Reçus')
                : folder === 'outbox'
                ? t('inbox.sent', 'Envoyés')
                : t('inbox.processed', 'Traités')}
              {folder === 'inbox' && (
                <Box component="small" sx={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>
                  · {t('inbox.unreadOf', { count: unreadCount, defaultValue: '{{count}} non lus' })}
                </Box>
              )}
            </Box>
            <Box sx={{ mt: '10px', position: 'relative' }}>
              <SearchIcon sx={{ position: 'absolute', left: 10, top: 8, fontSize: 17, color: C.muted }} />
              <Box
                component="input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('inbox.searchPlaceholder2', 'Rechercher un message, un contact, un objet…')}
                sx={{
                  width: '100%',
                  bgcolor: C.inner,
                  border: `1px solid ${C.border}`,
                  borderRadius: '10px',
                  p: '9px 12px 9px 34px',
                  color: '#fff',
                  fontSize: 12.5,
                  outline: 'none',
                  fontFamily: 'inherit',
                  '&::placeholder': { color: C.muted },
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '5px', p: '4px 16px 8px', flexWrap: 'wrap' }}>
            <Seg id="all" label={t('inbox.all', 'Tous')} />
            {folder === 'inbox' && (
              <Seg id="unread" label={`${t('inbox.unread', 'Non lus')}${unreadCount ? ` · ${unreadCount}` : ''}`} />
            )}
            <Seg id="important" label={t('inbox.importants', 'Importants')} />
            <Seg id="objects" label={t('inbox.objects', 'Objets')} />
          </Box>

          <Box sx={{ overflowY: 'auto', flex: 1, p: '4px 8px 20px' }}>
            {filtered.length === 0 && (
              <Box sx={{ px: 2, py: 4, textAlign: 'center', color: C.muted, fontSize: 13 }}>
                {t('inbox.noItemsAvailable', 'Aucun élément disponible actuellement.')}
              </Box>
            )}
            {filtered.map((msg) => {
              const unread = msg.status === 'non lu' && folder === 'inbox'
              const tp = msg.message_obj ? getObjType(msg.message_object?.setting_id) : null
              const other = otherUser(msg)
              return (
                <Box
                  key={msg.id}
                  onClick={() => openMessage(msg)}
                  sx={{
                    display: 'flex',
                    gap: '11px',
                    p: '12px 10px',
                    borderRadius: '11px',
                    cursor: 'pointer',
                    position: 'relative',
                    border:
                      selectedId === msg.id ? '1px solid rgba(91,155,245,.35)' : '1px solid transparent',
                    bgcolor: selectedId === msg.id ? C.card : 'transparent',
                    '&:hover': { bgcolor: selectedId === msg.id ? C.card : 'rgba(255,255,255,.05)' },
                    '&:hover .quick': { display: 'flex' },
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      minWidth: 9,
                      borderRadius: '50%',
                      bgcolor: C.blue,
                      mt: '14px',
                      opacity: unread ? 1 : 0,
                    }}
                  />
                  <UserAvatar user={other} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <Box
                        sx={{
                          fontSize: 13,
                          fontWeight: unread ? 700 : 600,
                          color: unread ? '#fff' : C.muted2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {other?.prenom} {other?.nom}
                      </Box>
                      <Box
                        sx={{
                          ml: 'auto',
                          fontSize: 10.5,
                          color: unread ? C.blue : C.muted,
                          fontWeight: unread ? 700 : 400,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fmtWhen(msg.timestamp)}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        fontSize: 12.5,
                        color: unread ? '#fff' : C.muted2,
                        fontWeight: unread ? 600 : 400,
                        mt: '2px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {msg.message_object?.default_subject || msg.subject}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '5px', minWidth: 0 }}>
                      {tp && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            px: '8px',
                            py: '2px',
                            borderRadius: '9px',
                            fontSize: 10,
                            fontWeight: 700,
                            bgcolor: tp.bg,
                            color: tp.color,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {tp.icon} {msg.message_object?.object_type}
                        </Box>
                      )}
                      {msg.message_object?.is_important && (
                        <Box sx={{ fontSize: 10, fontWeight: 700, color: C.red, whiteSpace: 'nowrap' }}>
                          ⚑ {t('inbox.important', 'Important')}
                        </Box>
                      )}
                      {msg.attachments_count > 0 && (
                        <Box sx={{ fontSize: 10.5, color: C.muted, whiteSpace: 'nowrap' }}>
                          📎 {msg.attachments_count}
                        </Box>
                      )}
                      {msg.comments_count > 0 && (
                        <Box sx={{ fontSize: 10.5, color: C.muted, whiteSpace: 'nowrap' }}>
                          💬 {msg.comments_count}
                        </Box>
                      )}
                      <Box
                        sx={{
                          fontSize: 11.5,
                          color: C.muted,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {msg.content}
                      </Box>
                    </Box>
                  </Box>
                  {folder !== 'outbox' && (
                    <Box className="quick" sx={{ position: 'absolute', right: 10, bottom: 8, display: 'none', gap: '6px' }}>
                      <Box
                        component="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          patchStatus(msg, msg.status === 'non lu' ? 'lu' : 'non lu')
                        }}
                        sx={{
                          bgcolor: C.inner,
                          border: `1px solid ${C.border}`,
                          color: C.muted2,
                          borderRadius: '7px',
                          fontSize: 10.5,
                          p: '3px 8px',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          '&:hover': { color: '#fff', borderColor: 'rgba(91,155,245,.5)' },
                        }}
                      >
                        {msg.status === 'non lu'
                          ? t('inbox.markRead', 'Marquer lu')
                          : t('inbox.markUnread', 'Marquer non lu')}
                      </Box>
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>

        {/* ===== Colonne 3 : détail ===== */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflowY: 'auto' }}>
          {selectedMessage ? (
            <>
              <Box sx={{ p: '20px 26px 14px', borderBottom: `1px solid ${C.border}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <Box sx={{ fontSize: 18, fontWeight: 700, flex: 1, minWidth: 0 }}>
                    {selectedMessage.message_object?.default_subject || selectedMessage.subject}
                  </Box>
                  {isInboxMsg(selectedMessage) && (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      <ActionBtn
                        onClick={() =>
                          patchStatus(selectedMessage, selectedMessage.status === 'non lu' ? 'lu' : 'non lu')
                        }
                      >
                        {selectedMessage.status === 'non lu'
                          ? `✉ ${t('inbox.markRead', 'Marquer lu')}`
                          : `✉ ${t('inbox.markUnread', 'Marquer non lu')}`}
                      </ActionBtn>
                      {selectedMessage.status !== 'traité' ? (
                        <ActionBtn primary onClick={() => patchStatus(selectedMessage, 'traité')}>
                          ✓ {t('inbox.markProcessed', 'Marquer traité')}
                        </ActionBtn>
                      ) : (
                        <ActionBtn onClick={() => patchStatus(selectedMessage, 'lu')}>
                          ↩ {t('inbox.reopen', 'Rouvrir')}
                        </ActionBtn>
                      )}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '12px' }}>
                  <UserAvatar user={otherUser(selectedMessage)} size={40} />
                  <Box>
                    <Box sx={{ fontSize: 13.5, fontWeight: 700 }}>
                      {otherUser(selectedMessage)?.prenom} {otherUser(selectedMessage)?.nom}
                    </Box>
                    <Box sx={{ fontSize: 11.5, color: C.muted, mt: '1px' }}>
                      {folder === 'outbox' ? t('inbox.sentOn', 'envoyé le') : t('inbox.receivedOn2', 'reçu le')}{' '}
                      {moment(selectedMessage.timestamp).format('DD/MM/YYYY HH:mm')}
                    </Box>
                  </Box>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {selectedMessage.message_object?.is_important && (
                      <Box
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          px: '10px',
                          py: '3px',
                          borderRadius: '10px',
                          bgcolor: 'rgba(248,113,113,.15)',
                          color: C.red,
                        }}
                      >
                        ⚑ {t('inbox.important', 'Important')}
                      </Box>
                    )}
                    {selectedMessage.message_obj && selectedMessage.message_object?.default_due_date > 0 && (
                      <Box
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          px: '10px',
                          py: '3px',
                          borderRadius: '10px',
                          bgcolor: 'rgba(251,191,36,.13)',
                          color: C.amber,
                        }}
                      >
                        {t('inbox.respondBefore', 'Répondre avant le :')}{' '}
                        {moment(selectedMessage.timestamp)
                          .add(selectedMessage.message_object?.default_due_date, 'days')
                          .format('DD/MM/YYYY')}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: '20px 26px 30px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {selectedMessage.content && (
                  <Box
                    sx={{
                      fontSize: 13.5,
                      lineHeight: 1.65,
                      color: C.muted2,
                      bgcolor: C.inner,
                      border: `1px solid ${C.border}`,
                      borderRadius: '12px',
                      p: '16px 18px',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {selectedMessage.content}
                  </Box>
                )}

                {selectedMessage.message_obj && (
                  <Box>
                    <SectionTitle>{t('inbox.sharedObject', 'OBJET PARTAGÉ')}</SectionTitle>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <ObjectCard obj={selectedMessage.message_object} primary />
                      {selectedMessage.message_obj_list?.map((o, i) => (
                        <ObjectCard key={i} obj={o} />
                      ))}
                    </Box>
                  </Box>
                )}

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                    <SectionTitle>
                      {t('inbox.attachments', 'PIÈCES JOINTES')} ({attachments.length})
                    </SectionTitle>
                    <Box sx={{ ml: 'auto', mb: '10px' }}>
                      <CreateDocForm mid={selectedMessage.id} setNewRows={setAttachments} />
                    </Box>
                  </Box>
                  {attachments.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
                      {attachments.map((a) => (
                        <Box
                          key={a.id}
                          component="a"
                          href={a.line_url || a.line}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '9px',
                            bgcolor: C.inner,
                            border: `1px solid ${C.border}`,
                            borderRadius: '10px',
                            p: '9px 13px',
                            textDecoration: 'none',
                            color: 'inherit',
                            '&:hover': { borderColor: 'rgba(91,155,245,.5)' },
                          }}
                        >
                          <Box sx={{ fontSize: 17 }}>📎</Box>
                          <Box>
                            <Box sx={{ fontSize: 12, fontWeight: 600 }}>
                              {(a.line || '').split('/').pop() || t('inbox.file', 'Fichier')}
                            </Box>
                            <Box sx={{ fontSize: 10.5, color: C.muted }}>
                              {a.fileSize ? `${a.fileSize} Mo · ` : ''}
                              {moment(a.uploaded_at).format('DD/MM/YYYY')}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ fontSize: 12, color: C.muted }}>
                      {t('inbox.noAttachments', 'Aucune pièce jointe.')}
                    </Box>
                  )}
                </Box>

                <Box>
                  <SectionTitle>
                    {t('inbox.discussion', 'DISCUSSION')} ({comments.length})
                  </SectionTitle>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mb: '14px' }}>
                    {comments.map((row) => {
                      const me =
                        row.user === siteUser.user?.id ||
                        row.user === siteUser.user?.pk ||
                        row.comment_user?.email === siteUser.user?.email
                      return (
                        <Box
                          key={row.id}
                          sx={{
                            display: 'flex',
                            gap: '10px',
                            maxWidth: '78%',
                            alignSelf: me ? 'flex-end' : 'flex-start',
                            flexDirection: me ? 'row-reverse' : 'row',
                          }}
                        >
                          <UserAvatar user={row.comment_user} size={30} />
                          <Box
                            sx={{
                              bgcolor: me ? 'rgba(45,95,184,.25)' : C.inner,
                              border: `1px solid ${me ? 'rgba(91,155,245,.3)' : C.border}`,
                              borderRadius: me ? '13px 3px 13px 13px' : '3px 13px 13px 13px',
                              p: '9px 13px',
                            }}
                          >
                            <Box sx={{ fontSize: 11, fontWeight: 700, color: me ? C.lblue : C.blue }}>
                              {me
                                ? t('inbox.me', 'Moi')
                                : `${row.comment_user?.prenom || ''} ${row.comment_user?.nom || ''}`}
                            </Box>
                            <Box sx={{ fontSize: 12.5, color: C.muted2, mt: '2px', lineHeight: 1.5 }}>
                              {row.content}
                            </Box>
                            <Box sx={{ fontSize: 9.5, color: C.muted, mt: '4px' }}>
                              {row.timestamp ? fmtWhen(row.timestamp) : ''}
                            </Box>
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'flex-end',
                      bgcolor: C.inner,
                      border: `1px solid ${C.border}`,
                      borderRadius: '13px',
                      p: '10px 12px',
                    }}
                  >
                    <Box
                      component="textarea"
                      rows={1}
                      value={commentInput}
                      onChange={(e) => setCommentInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          submitComment()
                        }
                      }}
                      placeholder={t('inbox.writeComment', 'Écrire un commentaire…')}
                      sx={{
                        flex: 1,
                        bgcolor: 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: '#fff',
                        fontSize: 12.5,
                        resize: 'none',
                        fontFamily: 'inherit',
                        lineHeight: 1.5,
                        '&::placeholder': { color: C.muted },
                      }}
                    />
                    <Button
                      onClick={submitComment}
                      sx={{
                        minWidth: 34,
                        width: 34,
                        height: 34,
                        bgcolor: C.btn,
                        color: '#fff',
                        borderRadius: '9px',
                        '&:hover': { bgcolor: C.btn, filter: 'brightness(1.15)' },
                      }}
                    >
                      <SendIcon sx={{ fontSize: 16 }} />
                    </Button>
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                color: C.muted,
                position: 'relative',
              }}
            >
              <Box sx={{ fontSize: 42, opacity: 0.5 }}>📥</Box>
              <Box sx={{ fontSize: 14, fontWeight: 600 }}>
                {t('inbox.selectMessage', 'Sélectionne un message pour l’afficher.')}
              </Box>
              <Box sx={{ position: 'absolute', right: 0, bottom: 0, display: 'flex' }}>
                <img src={blogo} alt="" style={{ height: 220, opacity: 0.5 }} />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}
